import { COUNTRY_RULES, type LibAddressInputRule } from '../data/countryRules';

export type CountryRequirements = {
  countryCode: string;
  requiredFlags: string;
  addressRequired: boolean;
  cityRequired: boolean;
  regionRequired: boolean;
  postalRequired: boolean;
  /** Whether HouseNr must be provided separately (PostNL structured address). */
  houseNumberRequired: boolean;
  postalRegexSource?: string;
  postalLabel: string;
  regionLabel: string;
};

export type ValidationError = { field: string; message: string; suggestion?: string };

export type ReceiverLike = {
  name?: string | null;
  street?: string | null;
  houseNumber?: string | null;
  houseNumberExt?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countryCode?: string | null;
  region?: string | null;
  phone?: string | null;
};

const DEFAULT_REQUIRE_FLAGS = 'AC';
const DEFAULT_ZIP_NAME_TYPE = 'postal';
const DEFAULT_STATE_NAME_TYPE = 'province';

// Some destinations commonly do not use house numbers (especially rural addresses),
// but still have a valid postal code (e.g. IE Eircode). Keep this list minimal and explicit.
const COUNTRIES_WITH_OPTIONAL_HOUSE_NUMBER = new Set([
  'IE', 'JP', 'CN', 'KR', 'TH', 'MY', 'SG', 'HK', 'TW', 'VN', 'PH', 'IN', 'AE', 'SA', 'IL', 'BR', 'MX',
]);

function normalizeCountryCode(input: string | null | undefined): string {
  const trimmed = (input || '').trim().toUpperCase();
  if (!trimmed) return '';
  if (trimmed === 'UK') return 'GB';
  return trimmed;
}

function countryCodeForRules(countryCode: string): string {
  // PostNL uses special territory codes (e.g. Canary Islands = IC). Map to ISO
  // country code for requirements/regex validation.
  if (countryCode === 'IC') return 'ES';
  return countryCode;
}

function titleCase(input: string): string {
  const raw = (input || '').trim();
  if (!raw) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function postalLabelFromRule(zipNameType?: string): string {
  const t = (zipNameType || '').trim().toLowerCase();
  if (t === 'zip') return 'ZIP code';
  if (t === 'postal') return 'Postal code';
  if (t === 'eircode') return 'Eircode';
  return 'Postal code';
}

function regionLabelFromRule(stateNameType?: string): string {
  const t = (stateNameType || '').trim();
  if (!t) return 'Region';
  const lower = t.toLowerCase();
  if (lower === 'state') return 'State';
  if (lower === 'province') return 'Province';
  if (lower === 'emirate') return 'Emirate';
  if (lower === 'area') return 'Area';
  if (lower === 'department') return 'Department';
  if (lower === 'prefecture') return 'Prefecture';
  return titleCase(t.split('_').join(' '));
}

function stripCountryPrefix(postalCode: string, cc: string): string {
  if (!cc || cc.length !== 2) return postalCode;
  return postalCode.replace(new RegExp(`^${cc}-?`, 'i'), '').trim();
}

function buildPostalRegex(countryCode: string): { regex?: RegExp; source?: string } {
  const cc = normalizeCountryCode(countryCode);
  const rulesCc = countryCodeForRules(cc);
  const rule: LibAddressInputRule | undefined = COUNTRY_RULES[rulesCc];
  const zip = rule?.zip;
  if (!zip || typeof zip !== 'string') return {};
  try {
    const anchored = `^(?:${zip})$`;
    return { regex: new RegExp(anchored, 'i'), source: zip };
  } catch {
    return {};
  }
}

export function getCountryRequirements(countryCode: string | null | undefined): CountryRequirements {
  const cc = normalizeCountryCode(countryCode);
  const rulesCc = countryCodeForRules(cc);
  const rule: LibAddressInputRule | undefined = rulesCc ? COUNTRY_RULES[rulesCc] : undefined;
  const explicitRequire = typeof rule?.require === 'string' && rule.require.trim().length > 0;
  const requiredFlags = (explicitRequire ? rule.require : DEFAULT_REQUIRE_FLAGS).toUpperCase();

  const addressRequired = requiredFlags.includes('A');
  const cityRequired = requiredFlags.includes('C');
  const regionRequired = explicitRequire ? requiredFlags.includes('S') : false;
  const postalRequired = explicitRequire ? requiredFlags.includes('Z') : !!rule?.zip;

  const { source } = buildPostalRegex(cc);

  const houseNumberRequired = postalRequired && !COUNTRIES_WITH_OPTIONAL_HOUSE_NUMBER.has(cc);

  return {
    countryCode: cc,
    requiredFlags,
    addressRequired,
    cityRequired,
    regionRequired,
    postalRequired,
    houseNumberRequired,
    postalRegexSource: source,
    postalLabel: postalLabelFromRule(rule?.zip_name_type || DEFAULT_ZIP_NAME_TYPE),
    regionLabel: regionLabelFromRule(rule?.state_name_type || DEFAULT_STATE_NAME_TYPE),
  };
}

export function normalizePostalCodeForCountry(postalCode: string, countryCode: string | null | undefined): string {
  const cc = normalizeCountryCode(countryCode);
  const raw = (postalCode || '').trim().toUpperCase();

  // Latvia official form is LV-NNNN (UPU); we also accept four digits alone and prepend LV- when complete.
  if (cc === 'LV') {
    const withoutPrefix = stripCountryPrefix(raw, cc);
    if (/^\d{4}$/.test(withoutPrefix)) {
      return `LV-${withoutPrefix}`;
    }
    return raw;
  }

  return stripCountryPrefix(raw, cc);
}

export function validatePostalCodeForCountry(
  postalCode: string,
  countryCode: string | null | undefined
): { valid: boolean; error?: string } {
  const cc = normalizeCountryCode(countryCode);
  if (!cc) return { valid: true };

  const req = getCountryRequirements(cc);
  const { regex } = buildPostalRegex(cc);
  const raw = (postalCode || '').trim().toUpperCase();
  const normalized = stripCountryPrefix(raw, cc);

  if (!normalized) return { valid: true };
  if (!regex) return { valid: true };

  if (!regex.test(raw) && !regex.test(normalized)) {
    return { valid: false, error: `Invalid ${req.postalLabel} format` };
  }

  return { valid: true };
}

export function validateReceiverAgainstCountryRequirements(receiver: ReceiverLike): ValidationError[] {
  const errors: ValidationError[] = [];

  const cc = normalizeCountryCode(receiver.countryCode);
  if (!cc) {
    errors.push({ field: 'countryCode', message: 'Country is required' });
    return errors;
  }

  const req = getCountryRequirements(cc);
  const { regex: postalRegex } = buildPostalRegex(cc);

  const street = (receiver.street || '').trim();
  const houseNumber = (receiver.houseNumber || '').trim();
  const city = (receiver.city || '').trim();
  const postalCodeRaw = (receiver.postalCode || '').trim();
  const regionRaw = (receiver.region || '').trim();

  if (!street || street.length < 2) {
    errors.push({ field: 'street', message: 'Street is required' });
  }

  if (req.houseNumberRequired && !houseNumber) {
    errors.push({
      field: 'houseNumber',
      message: 'House number is required',
      suggestion: 'Enter the house/building number separately from the street name',
    });
  }

  if (!city || city.length < 2) {
    errors.push({ field: 'city', message: 'City is required' });
  }

  if (req.regionRequired && !regionRaw) {
    errors.push({ field: 'region', message: `${req.regionLabel} is required for this destination` });
  }

  if (req.postalRequired && !postalCodeRaw) {
    errors.push({ field: 'postalCode', message: `${req.postalLabel} is required for this destination` });
  }

  const postalNormalized = stripCountryPrefix(postalCodeRaw.toUpperCase(), cc);
  if (
    postalNormalized &&
    postalRegex &&
    !postalRegex.test(postalCodeRaw.toUpperCase()) &&
    !postalRegex.test(postalNormalized)
  ) {
    errors.push({
      field: 'postalCode',
      message: `Invalid ${req.postalLabel} format`,
      suggestion: req.postalRegexSource ? `Expected pattern: ${req.postalRegexSource}` : undefined,
    });
  }

  return errors;
}

